/**
 * Pure plan of the file downloads an export produces. CSV with both data types
 * yields TWO files (`{base}.csv` + `{base}-companies.csv`); XLSX (multi-sheet)
 * and JSON (multi-key), or a single data type, yield one. The saga executes the
 * plan (fetch + trigger the browser download per entry).
 */
import type { ExportDataType } from '@shared/schemas';

import {
  EXPORT_FORMAT_CONFIG,
  type ExportFormat,
  resolveDataType,
  type ResolvedDataType,
} from './export-plan.model';

/** Delay between the two CSV downloads so the browser doesn't merge them. */
export const CSV_DOWNLOAD_DELAY_MS = 300;

export interface PlannedDownload {
  /** `data_type` of the request that produces this file. */
  data_type: ResolvedDataType;
  filename: string;
}

/** Sanitise a user-provided name into a safe, lowercase filename slug. */
export function slugify(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // strip accents
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-') // non-alphanum → dash
    .replace(/^-+|-+$/g, ''); // trim leading/trailing dashes
}

export interface PlanDownloadsInput {
  format: ExportFormat;
  dataTypes: ExportDataType[];
  /** Raw base name (list name or page name); slugged here, 'export' fallback. */
  baseName: string;
}

export function planDownloads({ format, dataTypes, baseName }: PlanDownloadsInput): PlannedDownload[] {
  const base = slugify(baseName || 'export') || 'export';
  const ext = EXPORT_FORMAT_CONFIG[format].ext;
  const resolved = resolveDataType(dataTypes);

  // CSV + both → two separate downloads (one per data type) for readability.
  if (format === 'csv' && resolved === 'both') {
    return [
      { data_type: 'profiles', filename: `${base}.${ext}` },
      { data_type: 'companies', filename: `${base}-companies.${ext}` },
    ];
  }

  const filename =
    resolved === 'companies' ? `${base}-companies.${ext}` : `${base}.${ext}`;
  return [{ data_type: resolved, filename }];
}
